import { z } from 'zod';
import type { AdminPageMeta, UserAdminAuditAction } from './admin.js';
import { queryBoolean } from './query.js';
import type { VenueAdminAuditAction } from './admin-venue.js';
import type { B2bAuditAction } from './b2b-audit.js';

/**
 * F09 v2 — Verified Profiles (per-entity blue tick) frozen API contracts.
 *
 * Verification is PER ENTITY RECORD (D1): one person can hold a player tick
 * (`users`) AND a trainer tick (`trainer_profiles`); one B2B account can hold a
 * club/organizer tick while its venue holds a venue tick.
 *
 *   player    = `users` row
 *   trainer   = `trainer_profiles` row (PK = user id)
 *   venue     = `venues` row
 *   club      = `b2b_accounts` row with `account_kind='club'`
 *   organizer = `b2b_accounts` row with `account_kind='organizer'`
 *
 * Three vocabularies (E2) — never mix them:
 *   - REQUEST status  (`verifications.status`, 8 values) → `VerificationRequestStatus`
 *   - ENTITY column   (`<table>.verification_status`, 2 values) → `EntityVerificationStatus`
 *   - OWNER state     (derived by the API's `deriveOwnerEntityState()`, 7 values)
 *                     → `OwnerVerificationState` (+ a `nameChange` sub-state)
 *
 * Public shapes carry BOOLEANS only (`verified`, `<role>Verified`), never a
 * status value (E32). The UI renders the tick iff that boolean is true.
 *
 * Wave-2 tracks develop against these contracts in parallel — DO NOT rename /
 * drop / add a field without escalating to the orchestrator (amendment
 * protocol). The document-policy version is server-only (E21) and the
 * transactional notification-kind list lives in `@bravemove/i18n` — neither is
 * exported here.
 *
 * The v1 surface (athlete/trainer/club/brand/federation `VerifiedType`, the
 * 5-value v1 `VerificationStatus`, the v1 row/projection shapes and Zod
 * schemas) was removed in the W3 coupling sweep (T3.2).
 *
 * See plans/09_F09_verified_profiles.md §2.3 / §5 / §6 / §13.
 */

// ═════════════════════════════════════════════════════════════════════════════
// v2 — enums
// ═════════════════════════════════════════════════════════════════════════════

/** The five verifiable entity kinds (D1). */
export const VERIFIABLE_ENTITY_TYPES = ['player', 'trainer', 'venue', 'club', 'organizer'] as const;
export type VerifiableEntityType = (typeof VERIFIABLE_ENTITY_TYPES)[number];
export const VerifiableEntityTypeSchema = z.enum(VERIFIABLE_ENTITY_TYPES);

/** Entity types whose requester is the human themself (E28 — mobile can act on these). */
export const VERIFICATION_SELF_ENTITY_TYPES = [
  'player',
  'trainer',
] as const satisfies readonly VerifiableEntityType[];
/** Entity types whose requester must be the B2B account owner (E18); `entityId` is required. */
export const VERIFICATION_B2B_ENTITY_TYPES = [
  'venue',
  'club',
  'organizer',
] as const satisfies readonly VerifiableEntityType[];
/** Entity types that carry a legal name (`b2b_accounts.legal_business_name`) — E27. */
export const VERIFICATION_LEGAL_NAME_ENTITY_TYPES = [
  'club',
  'organizer',
] as const satisfies readonly VerifiableEntityType[];

/** A polymorphic pointer to one verifiable entity. Player + trainer ids are the user id. */
export type EntityRef = {
  readonly type: VerifiableEntityType;
  readonly id: string;
};

export const VERIFICATION_REQUEST_KINDS = ['initial', 'name_change'] as const;
export type VerificationRequestKind = (typeof VERIFICATION_REQUEST_KINDS)[number];
export const VerificationRequestKindSchema = z.enum(VERIFICATION_REQUEST_KINDS);

export const VERIFICATION_SOURCES = ['request', 'admin_direct', 'legacy_backfill'] as const;
export type VerificationSource = (typeof VERIFICATION_SOURCES)[number];
export const VerificationSourceSchema = z.enum(VERIFICATION_SOURCES);

/** `verifications.status` (E2 request vocabulary). */
export const VERIFICATION_REQUEST_STATUSES = [
  'draft',
  'pending',
  'more_info_needed',
  'approved',
  'rejected',
  'withdrawn',
  'revoked',
  'superseded',
] as const;
export type VerificationRequestStatus = (typeof VERIFICATION_REQUEST_STATUSES)[number];
export const VerificationRequestStatusSchema = z.enum(VERIFICATION_REQUEST_STATUSES);

/** The open (non-terminal) request statuses — at most one open request per entity per kind (E5). */
export const OPEN_VERIFICATION_REQUEST_STATUSES = [
  'draft',
  'pending',
  'more_info_needed',
] as const satisfies readonly VerificationRequestStatus[];
export type OpenVerificationRequestStatus = (typeof OPEN_VERIFICATION_REQUEST_STATUSES)[number];

/** The terminal request statuses (everything that is not open). */
export const TERMINAL_VERIFICATION_REQUEST_STATUSES = [
  'approved',
  'rejected',
  'withdrawn',
  'revoked',
  'superseded',
] as const satisfies readonly VerificationRequestStatus[];
export type TerminalVerificationRequestStatus =
  (typeof TERMINAL_VERIFICATION_REQUEST_STATUSES)[number];

/** `<entity table>.verification_status` (E3) — the ONLY column the tick reads. */
export const ENTITY_VERIFICATION_STATUSES = ['unverified', 'verified'] as const;
export type EntityVerificationStatus = (typeof ENTITY_VERIFICATION_STATUSES)[number];
export const EntityVerificationStatusSchema = z.enum(ENTITY_VERIFICATION_STATUSES);

/** Owner-facing derived state (E2 / D4). Admin rows carry it as `verificationState`. */
export const OWNER_VERIFICATION_STATES = [
  'not_requested',
  'draft',
  'pending',
  'more_info_needed',
  'verified',
  'rejected',
  'revoked',
] as const;
export type OwnerVerificationState = (typeof OWNER_VERIFICATION_STATES)[number];
export const OwnerVerificationStateSchema = z.enum(OWNER_VERIFICATION_STATES);

/** The `nameChange` sub-state of an owner state (E2 / E26). Outcomes ride `latestTerminal`. */
export const OWNER_NAME_CHANGE_STATES = ['none', 'draft', 'pending', 'more_info_needed'] as const;
export type OwnerNameChangeState = (typeof OWNER_NAME_CHANGE_STATES)[number];

/** Evidence document kinds (E6). Notability sub-types are helper copy only. */
export const VERIFICATION_DOCUMENT_KINDS = [
  'government_id',
  'certification',
  'business_registration',
  'federation_affiliation',
  'notability_evidence',
  'other',
] as const;
export type VerificationDocumentKind = (typeof VERIFICATION_DOCUMENT_KINDS)[number];
export const VerificationDocumentKindSchema = z.enum(VERIFICATION_DOCUMENT_KINDS);

/** Government-ID document type the admin records at approve (E9 — stored, NOT hashed). */
export const ID_DOCUMENT_TYPES = [
  'national_id',
  'passport',
  'driving_license',
  'residence_permit',
  'other',
] as const;
export type IdDocumentType = (typeof ID_DOCUMENT_TYPES)[number];
export const IdDocumentTypeSchema = z.enum(ID_DOCUMENT_TYPES);

/** Rejection categories (E15). The user sees the category text + optional `note`. */
export const VERIFICATION_REASON_CATEGORIES = [
  'document_unreadable',
  'document_expired',
  'name_mismatch',
  'insufficient_evidence',
  'prerequisite_not_met',
  'not_notable',
  'duplicate_identity',
  'suspected_fraud',
  'ineligible_entity',
  'other',
] as const;
export type VerificationReasonCategory = (typeof VERIFICATION_REASON_CATEGORIES)[number];
export const VerificationReasonCategorySchema = z.enum(VERIFICATION_REASON_CATEGORIES);

/** Revocation categories (E16). */
export const VERIFICATION_REVOCATION_CATEGORIES = [
  'fraud',
  'impersonation',
  'prerequisite_lapsed',
  'policy_violation',
  'ownership_changed',
  'requested_by_holder',
  'entity_merged',
  'entity_deleted',
  'other',
] as const;
export type VerificationRevocationCategory = (typeof VERIFICATION_REVOCATION_CATEGORIES)[number];
export const VerificationRevocationCategorySchema = z.enum(VERIFICATION_REVOCATION_CATEGORIES);

/** System-only revocation categories (E16 — set `rerequest_requires_admin=false`). */
export const SYSTEM_REVOCATION_CATEGORIES = [
  'entity_merged',
  'entity_deleted',
] as const satisfies readonly VerificationRevocationCategory[];
export type SystemRevocationCategory = (typeof SYSTEM_REVOCATION_CATEGORIES)[number];

/** Categories an admin may pick (E16 — excludes `entity_merged` + `entity_deleted`). */
export const ADMIN_REVOCATION_CATEGORIES = [
  'fraud',
  'impersonation',
  'prerequisite_lapsed',
  'policy_violation',
  'ownership_changed',
  'requested_by_holder',
  'other',
] as const satisfies readonly VerificationRevocationCategory[];
export type AdminRevocationCategory = (typeof ADMIN_REVOCATION_CATEGORIES)[number];
export const AdminRevocationCategorySchema = z.enum(ADMIN_REVOCATION_CATEGORIES);

/** `verifications.withdrawn_reason` (§4.1). `legacy_reset` rows are invisible to the owner derivation (E2). */
export const VERIFICATION_WITHDRAWN_REASONS = [
  'requester',
  'abandoned_draft',
  'more_info_timeout',
  'entity_merged',
  'entity_deleted',
  'requester_deleted',
  'tick_revoked',
  'legacy_reset',
] as const;
export type VerificationWithdrawnReason = (typeof VERIFICATION_WITHDRAWN_REASONS)[number];
export const VerificationWithdrawnReasonSchema = z.enum(VERIFICATION_WITHDRAWN_REASONS);

/** `verification_candidates.kind` (E8). */
export const VERIFICATION_CANDIDATE_KINDS = ['candidate', 'watchlist'] as const;
export type VerificationCandidateKind = (typeof VERIFICATION_CANDIDATE_KINDS)[number];
export const VerificationCandidateKindSchema = z.enum(VERIFICATION_CANDIDATE_KINDS);

/** Status of a `kind='candidate'` row. */
export const CANDIDATE_STATUSES = ['open', 'invited', 'dismissed', 'converted'] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];
export const CandidateStatusSchema = z.enum(CANDIDATE_STATUSES);

/** Status of a `kind='watchlist'` row. */
export const WATCHLIST_STATUSES = ['open', 'dismissed', 'resolved'] as const;
export type WatchlistStatus = (typeof WATCHLIST_STATUSES)[number];
export const WatchlistStatusSchema = z.enum(WATCHLIST_STATUSES);

/** Watchlist resolutions (E8): `dismissed` = false positive; `resolved` = reviewed, tick kept. */
export const WATCHLIST_RESOLUTIONS = [
  'dismissed',
  'resolved',
] as const satisfies readonly WatchlistStatus[];
export type WatchlistResolution = (typeof WATCHLIST_RESOLUTIONS)[number];
export const WatchlistResolutionSchema = z.enum(WATCHLIST_RESOLUTIONS);

/** Hard prerequisites checked by the API's prerequisite resolver (E12 / E17 / E39). */
export const VERIFICATION_PREREQUISITE_CODES = [
  'subject_account_active',
  'trainer_profile_exists',
  'trainer_has_verified_cert',
  'trainer_not_suspended',
  'trainer_not_deactivated',
  'venue_active',
  'venue_claimed',
  'owner_account_active',
  'owner_unchanged',
  'b2b_account_active',
  'b2b_account_kind_matches',
  'organizer_not_suspended',
] as const;
export type VerificationPrerequisiteCode = (typeof VERIFICATION_PREREQUISITE_CODES)[number];
export const VerificationPrerequisiteCodeSchema = z.enum(VERIFICATION_PREREQUISITE_CODES);

/** The resolver phases (§7 `check(db, ref, { phase })`). */
export const VERIFICATION_PREREQUISITE_PHASES = [
  'create',
  'submit',
  'approve',
  'direct_verify',
] as const;
export type VerificationPrerequisitePhase = (typeof VERIFICATION_PREREQUISITE_PHASES)[number];

/** Codes the `direct_verify` phase skips (E12 — it checks every other hard prerequisite). */
export const DIRECT_VERIFY_EXCLUDED_PREREQUISITES = [
  'owner_unchanged',
] as const satisfies readonly VerificationPrerequisiteCode[];

/** One unmet hard prerequisite (the resolver's output element). */
export type PrerequisiteFailure = {
  readonly code: VerificationPrerequisiteCode;
  readonly hard: true;
};

/** Reasons the candidate worker surfaces an unverified entity (§9). */
export const CANDIDATE_REASON_CODES = [
  'player_high_skill',
  'player_admin_verified_skill',
  'player_many_matches',
  'player_team_captain',
  'trainer_verified_certs',
  'trainer_many_sessions',
  'trainer_high_rating',
  'trainer_verified_venue_affiliation',
  'venue_claimed_active',
  'venue_paid_tier',
  'venue_high_activity',
  'venue_profile_complete',
  'club_active_program',
  'club_many_players',
  'organizer_federation',
  'organizer_approved',
  'organizer_completed_tournaments',
  'owner_player_verified',
  'account_tenure',
] as const;
export type CandidateReasonCode = (typeof CANDIDATE_REASON_CODES)[number];
export const CandidateReasonCodeSchema = z.enum(CANDIDATE_REASON_CODES);

/** Reasons a verified entity lands on the watchlist (§9 hooks; nothing auto-revokes). */
export const WATCHLIST_REASON_CODES = [
  'trainer_cert_lapsed',
  'trainer_suspended',
  'trainer_deactivated',
  'venue_claim_lapsed',
  'venue_owner_changed',
  'venue_closed',
  'venue_suspended',
  'b2b_account_suspended',
  'b2b_account_owner_changed',
  'organizer_suspended',
  'subject_user_suspended',
  'subject_user_deleted',
  'new_reports',
  'impersonation_report',
  'merged_from_verified_venue',
  'duplicate_identity',
  'prerequisite_lapsed',
  'legacy_rereview',
  'legacy_v1_club_unresolved',
  'legacy_v1_organizer_unresolved',
] as const;
export type WatchlistReasonCode = (typeof WATCHLIST_REASON_CODES)[number];
export const WatchlistReasonCodeSchema = z.enum(WATCHLIST_REASON_CODES);

/** Risk flags that CAP the strength at `weak` regardless of score (E8). */
export const VERIFICATION_CAPPING_RISK_FLAGS = [
  'open_reports',
  'prior_revocation',
  'duplicate_identity_hmac',
  'duplicate_document_sha',
  'subject_suspended',
] as const;
/** Risk flags that subtract points from the score (server-only weights). */
export const VERIFICATION_PENALTY_RISK_FLAGS = [
  'actioned_reports_365d',
  'prior_rejection',
  'prior_withdrawal',
  'resubmitted_identical_document',
  'name_changed_since_submit',
] as const;
/** Every risk flag (capping first, then penalty). Stored in `evaluator_risk_flags text[]`. */
export const VERIFICATION_RISK_FLAGS = [
  ...VERIFICATION_CAPPING_RISK_FLAGS,
  ...VERIFICATION_PENALTY_RISK_FLAGS,
] as const;
export type VerificationCappingRiskFlag = (typeof VERIFICATION_CAPPING_RISK_FLAGS)[number];
export type VerificationPenaltyRiskFlag = (typeof VERIFICATION_PENALTY_RISK_FLAGS)[number];
export type VerificationRiskFlag = (typeof VERIFICATION_RISK_FLAGS)[number];
export const VerificationRiskFlagSchema = z.enum(VERIFICATION_RISK_FLAGS);

/** `verification_events.event_kind` — types list only, no DB CHECK (§4.3). */
export const VERIFICATION_EVENT_KINDS = [
  'request_created',
  'document_uploaded',
  'document_removed',
  'submitted',
  'resubmitted',
  'more_info_requested',
  'approved',
  'rejected',
  'withdrawn',
  'superseded',
  'revoked',
  'cooldown_lifted',
  'rerequest_unlocked',
  'direct_verified',
  'name_change_applied',
  'name_lock_blocked',
  'name_lock_override',
  'candidate_invited',
  'candidate_dismissed',
  'watchlist_flagged',
  'watchlist_resolved',
  'duplicate_identity_flagged',
  'entity_merged',
  'legacy_backfill',
] as const;
export type VerificationEventKind = (typeof VERIFICATION_EVENT_KINDS)[number];
export const VerificationEventKindSchema = z.enum(VERIFICATION_EVENT_KINDS);

/** `verification_events.actor_kind` (E36). */
export const VERIFICATION_EVENT_ACTOR_KINDS = ['requester', 'admin', 'system'] as const;
export type VerificationEventActorKind = (typeof VERIFICATION_EVENT_ACTOR_KINDS)[number];

/** Evaluator strength bands (E8). */
export const VERIFICATION_STRENGTHS = ['strong', 'moderate', 'weak'] as const;
export type VerificationStrength = (typeof VERIFICATION_STRENGTHS)[number];
export const VerificationStrengthSchema = z.enum(VERIFICATION_STRENGTHS);

/** `verification_document_access_log.render_mode` (E7 / §8 — legacy HEIC is `download`). */
export const VERIFICATION_DOC_RENDER_MODES = ['original', 'download'] as const;
export type VerificationDocRenderMode = (typeof VERIFICATION_DOC_RENDER_MODES)[number];

/** Admin queue sort (§6 `GET /admin/verifications`). */
export const VERIFICATION_QUEUE_SORTS = ['fifo', 'score'] as const;
export type VerificationQueueSort = (typeof VERIFICATION_QUEUE_SORTS)[number];

/** Why `canRequest` is false on an owner state — reuses the matching error codes. */
export const VERIFICATION_BLOCKED_REASONS = [
  'ALREADY_VERIFIED',
  'REQUEST_ALREADY_OPEN',
  'COOLDOWN_ACTIVE',
  'REREQUEST_REQUIRES_SUPPORT',
  'PREREQUISITE_NOT_MET',
  'NOT_ENTITY_OWNER',
] as const;
export type VerificationBlockedReason = (typeof VERIFICATION_BLOCKED_REASONS)[number];

// ═════════════════════════════════════════════════════════════════════════════
// v2 — constants
// ═════════════════════════════════════════════════════════════════════════════

/** Per-document upload cap: 10 MB (unchanged from v1). */
export const VERIFICATION_DOC_MAX_BYTES = 10 * 1024 * 1024;
/** Total documents per request (E6). Was 5 in v1. */
export const VERIFICATION_DOC_MAX_COUNT = 12;
/** Per-kind document caps (E6: 3/5/3/3/5/3). */
export const VERIFICATION_DOC_KIND_MAX: Readonly<Record<VerificationDocumentKind, number>> = {
  government_id: 3,
  certification: 5,
  business_registration: 3,
  federation_affiliation: 3,
  notability_evidence: 5,
  other: 3,
};
/** Mime types accepted for NEW uploads (E6 — HEIC dropped). */
export const VERIFICATION_ALLOWED_UPLOAD_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export type VerificationUploadMime = (typeof VERIFICATION_ALLOWED_UPLOAD_MIMES)[number];
/** Mime types a STORED document row may carry (the DB CHECK keeps the v1 set so legacy HEIC rows stay valid). */
export const VERIFICATION_STORED_DOC_MIMES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
] as const;
export type VerificationStoredDocMime = (typeof VERIFICATION_STORED_DOC_MIMES)[number];

/** Initial-request rejection cooldown (D9). Name-change rejections have none (E26). */
export const VERIFICATION_REJECTION_COOLDOWN_DAYS = 30;
/** Default more-info due window (E11); admins may pick 7–30. */
export const VERIFICATION_MORE_INFO_DUE_DAYS = 30;
export const VERIFICATION_MORE_INFO_DUE_DAYS_MIN = 7;
export const VERIFICATION_MORE_INFO_DUE_DAYS_MAX = 30;
/** Drafts untouched this long → `withdrawn(abandoned_draft)` (E11). */
export const VERIFICATION_DRAFT_ABANDON_DAYS = 30;
/** A dismissed candidate resurfaces after this many days, only if its signals changed (E11). */
export const VERIFICATION_CANDIDATE_RESURFACE_DAYS = 90;
/** Re-invite blocked for this many days (E11). */
export const VERIFICATION_INVITE_COOLDOWN_DAYS = 90;
/** Minimum evaluator score to become a candidate (§9). */
export const VERIFICATION_CANDIDATE_MIN_SCORE = 75;
/** Strength band floors on the 0–100 score (E8): strong ≥75, moderate 50–74, weak <50. */
export const STRENGTH_THRESHOLDS = { strong: 75, moderate: 50 } as const;

// Text-field bounds shared by the Zod inputs below.
export const VERIFICATION_REQUESTED_NAME_MIN = 2;
export const VERIFICATION_REQUESTED_NAME_MAX = 200;
export const VERIFICATION_REQUESTED_LEGAL_NAME_MIN = 3;
export const VERIFICATION_REQUESTED_LEGAL_NAME_MAX = 200;
export const VERIFICATION_NOTE_MAX = 1000;
export const VERIFICATION_INTERNAL_NOTE_MIN = 10;
export const VERIFICATION_INTERNAL_NOTE_MAX = 500;
export const VERIFICATION_REASON_MIN = 10;
export const VERIFICATION_REASON_MAX = 500;
export const VERIFICATION_REVOCATION_REASON_MAX = 1000;
export const VERIFICATION_MORE_INFO_PROMPT_MIN = 10;
export const VERIFICATION_MORE_INFO_PROMPT_MAX = 1000;
export const VERIFICATION_INVITE_MESSAGE_MAX = 500;
export const VERIFICATION_RESURFACE_AFTER_DAYS_MIN = 30;
export const VERIFICATION_RESURFACE_AFTER_DAYS_MAX = 365;
export const VERIFICATION_ID_NUMBER_MAX = 64;

// ═════════════════════════════════════════════════════════════════════════════
// v2 — criteria (single source of truth for API validation, evaluator,
// explainer and the wizards)
// ═════════════════════════════════════════════════════════════════════════════

/** Who may file a request: the human themself, or the owning B2B account's owner (E18). */
export type VerificationRequesterRule = 'self' | 'b2b_owner';

/** API field names of an entity's lockable name columns (E4 / E13 `field`). */
export const VERIFICATION_NAME_FIELDS = ['displayName', 'name', 'legalBusinessName'] as const;
export type VerificationNameField = (typeof VERIFICATION_NAME_FIELDS)[number];

/**
 * One required evidence group. Satisfied when the request holds at least
 * `minCount` documents whose kind is in `anyOf`. `labelKey` is the i18n key
 * (`verification:criteria.<entityType>.<group>`); a name-change group reuses the
 * label of the initial group with the same evidence.
 */
export type VerificationDocumentGroup = {
  readonly anyOf: readonly VerificationDocumentKind[];
  readonly minCount: number;
  readonly labelKey: `verification:criteria.${VerifiableEntityType}.${string}`;
};

export type VerificationCriteria = {
  readonly entityType: VerifiableEntityType;
  readonly requesterRule: VerificationRequesterRule;
  /** Groups required for an `initial` request (re-checked at approve except `admin_direct` — E23). */
  readonly requiredDocuments: readonly VerificationDocumentGroup[];
  /** Groups required for a `name_change` request. */
  readonly nameChangeRequiredDocuments: readonly VerificationDocumentGroup[];
  /** Kinds that may additionally be uploaded but never satisfy a requirement. */
  readonly optionalDocumentKinds: readonly VerificationDocumentKind[];
  /** Checked at create / submit / approve; `direct_verify` skips `DIRECT_VERIFY_EXCLUDED_PREREQUISITES`. */
  readonly hardPrerequisites: readonly VerificationPrerequisiteCode[];
  /**
   * Stable snake_case ids of the soft signals that strengthen a request (the
   * explainer's "what helps" bullets; mirrors the §9 evaluator signal table).
   * Signals never block — only `hardPrerequisites` do.
   */
  readonly softSignalKeys: readonly string[];
  /** Lockable name columns: `primary` ↔ `requestedName`, `legal` ↔ `requestedLegalName`. */
  readonly nameFields: {
    readonly primary: VerificationNameField;
    readonly legal: VerificationNameField | null;
  };
  /** i18n key of the explainer section for this entity type. */
  readonly explainerKey: `verification:explainer.kinds.${VerifiableEntityType}`;
};

export const VERIFICATION_CRITERIA: Readonly<Record<VerifiableEntityType, VerificationCriteria>> = {
  player: {
    entityType: 'player',
    requesterRule: 'self',
    requiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.player.government_id',
      },
      {
        anyOf: ['notability_evidence'],
        minCount: 1,
        labelKey: 'verification:criteria.player.notability_evidence',
      },
    ],
    nameChangeRequiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.player.government_id',
      },
    ],
    optionalDocumentKinds: ['other'],
    hardPrerequisites: ['subject_account_active'],
    softSignalKeys: [
      'account_age',
      'email_phone_verified',
      'skill_level',
      'matches_played',
      'admin_verified_skill',
      'team_captaincy',
      'badges',
      'profile_completeness',
      'clean_report_history',
    ],
    nameFields: { primary: 'displayName', legal: null },
    explainerKey: 'verification:explainer.kinds.player',
  },
  trainer: {
    entityType: 'trainer',
    requesterRule: 'self',
    requiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.trainer.government_id',
      },
    ],
    nameChangeRequiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.trainer.government_id',
      },
    ],
    optionalDocumentKinds: ['certification', 'other'],
    hardPrerequisites: [
      'subject_account_active',
      'trainer_profile_exists',
      'trainer_has_verified_cert',
      'trainer_not_suspended',
      'trainer_not_deactivated',
    ],
    softSignalKeys: [
      'verified_certifications',
      'completed_sessions',
      'distinct_clients',
      'rating',
      'verified_venue_affiliations',
      'years_experience',
      'clean_report_history',
    ],
    nameFields: { primary: 'displayName', legal: null },
    explainerKey: 'verification:explainer.kinds.trainer',
  },
  venue: {
    entityType: 'venue',
    requesterRule: 'b2b_owner',
    requiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.venue.government_id',
      },
      {
        anyOf: ['business_registration'],
        minCount: 1,
        labelKey: 'verification:criteria.venue.business_registration',
      },
    ],
    nameChangeRequiredDocuments: [
      {
        anyOf: ['business_registration'],
        minCount: 1,
        labelKey: 'verification:criteria.venue.business_registration',
      },
    ],
    optionalDocumentKinds: ['other'],
    hardPrerequisites: ['venue_active', 'venue_claimed', 'owner_account_active', 'owner_unchanged'],
    softSignalKeys: [
      'approved_claim',
      'subscription_tier',
      'recent_bookings',
      'profile_completeness',
      'approved_photos',
      'owner_player_verified',
      'clean_moderation_history',
    ],
    nameFields: { primary: 'name', legal: null },
    explainerKey: 'verification:explainer.kinds.venue',
  },
  club: {
    entityType: 'club',
    requesterRule: 'b2b_owner',
    requiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.club.government_id',
      },
      {
        anyOf: ['business_registration', 'federation_affiliation'],
        minCount: 1,
        labelKey: 'verification:criteria.club.registration_or_federation',
      },
    ],
    nameChangeRequiredDocuments: [
      {
        anyOf: ['business_registration', 'federation_affiliation'],
        minCount: 1,
        labelKey: 'verification:criteria.club.registration_or_federation',
      },
    ],
    optionalDocumentKinds: ['other'],
    hardPrerequisites: ['b2b_account_active', 'b2b_account_kind_matches'],
    softSignalKeys: [
      'managed_teams',
      'club_trainers',
      'players',
      'completed_training_sessions',
      'account_age',
    ],
    nameFields: { primary: 'displayName', legal: 'legalBusinessName' },
    explainerKey: 'verification:explainer.kinds.club',
  },
  organizer: {
    entityType: 'organizer',
    requesterRule: 'b2b_owner',
    requiredDocuments: [
      {
        anyOf: ['government_id'],
        minCount: 1,
        labelKey: 'verification:criteria.organizer.government_id',
      },
      {
        anyOf: ['business_registration', 'federation_affiliation'],
        minCount: 1,
        labelKey: 'verification:criteria.organizer.registration_or_federation',
      },
    ],
    nameChangeRequiredDocuments: [
      {
        anyOf: ['business_registration', 'federation_affiliation'],
        minCount: 1,
        labelKey: 'verification:criteria.organizer.registration_or_federation',
      },
    ],
    optionalDocumentKinds: ['other'],
    hardPrerequisites: [
      'b2b_account_active',
      'b2b_account_kind_matches',
      'organizer_not_suspended',
    ],
    softSignalKeys: [
      'federation_status',
      'organizer_approved',
      'completed_tournaments',
      'registrations',
      'approved_hosts',
    ],
    nameFields: { primary: 'displayName', legal: 'legalBusinessName' },
    explainerKey: 'verification:explainer.kinds.organizer',
  },
};

/**
 * The hard prerequisites the resolver checks for `entityType` in `phase` (E12):
 * every `hardPrerequisites` code, minus `owner_unchanged` for `direct_verify`.
 */
export function prerequisitesForPhase(
  entityType: VerifiableEntityType,
  phase: VerificationPrerequisitePhase,
): readonly VerificationPrerequisiteCode[] {
  const all = VERIFICATION_CRITERIA[entityType].hardPrerequisites;
  if (phase !== 'direct_verify') return all;
  const excluded: readonly VerificationPrerequisiteCode[] = DIRECT_VERIFY_EXCLUDED_PREREQUISITES;
  return all.filter((code) => !excluded.includes(code));
}

/**
 * The document groups a request of `requestKind` must satisfy (E23 / §12).
 */
export function requiredDocumentGroups(
  entityType: VerifiableEntityType,
  requestKind: VerificationRequestKind,
): readonly VerificationDocumentGroup[] {
  const c = VERIFICATION_CRITERIA[entityType];
  return requestKind === 'name_change' ? c.nameChangeRequiredDocuments : c.requiredDocuments;
}

/**
 * The groups NOT yet satisfied by `kinds` (one entry per uploaded document).
 * Empty array = every group is met.
 */
export function missingDocumentGroups(
  groups: readonly VerificationDocumentGroup[],
  kinds: readonly VerificationDocumentKind[],
): readonly VerificationDocumentGroup[] {
  return groups.filter((g) => kinds.filter((k) => g.anyOf.includes(k)).length < g.minCount);
}

/** Every kind a request for `entityType` may carry (required groups ∪ optional kinds). */
export function allowedDocumentKinds(
  entityType: VerifiableEntityType,
): readonly VerificationDocumentKind[] {
  const c = VERIFICATION_CRITERIA[entityType];
  const set = new Set<VerificationDocumentKind>(c.optionalDocumentKinds);
  for (const g of [...c.requiredDocuments, ...c.nameChangeRequiredDocuments]) {
    for (const k of g.anyOf) set.add(k);
  }
  return VERIFICATION_DOCUMENT_KINDS.filter((k) => set.has(k));
}

// ═════════════════════════════════════════════════════════════════════════════
// v2 — helpers
// ═════════════════════════════════════════════════════════════════════════════

/**
 * The shared name subject (E5 / E26): player and trainer both lock
 * `users.display_name`, so they map to `'user'`.
 */
export type VerificationNameSubject = 'user' | 'venue' | 'b2b_account';
export function nameSubjectOf(entityType: VerifiableEntityType): VerificationNameSubject {
  switch (entityType) {
    case 'player':
    case 'trainer':
      return 'user';
    case 'venue':
      return 'venue';
    case 'club':
    case 'organizer':
      return 'b2b_account';
  }
}

/**
 * E4 — the name is locked iff ANY of the given entity statuses is `verified`.
 * Pass both the player and trainer statuses for `users.display_name` (either
 * tick locks it). Accepts raw column strings so legacy 4-valued trainer/venue
 * columns are tolerated during the Wave-2 transition (`pending` → unlocked).
 */
export function isNameLocked(...statuses: ReadonlyArray<string | null | undefined>): boolean {
  return statuses.some((s) => s === 'verified');
}

/** One tick holder that locks a name (E13 `lockedBy[]`). */
export type NameLockHolder = {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
};

/** `MeResponse.displayNameLock` (E20). `lockedBy[0]` is the player tick if held (E26). */
export type DisplayNameLock = {
  readonly locked: boolean;
  readonly lockedBy: readonly NameLockHolder[];
};

/**
 * Builds the `users.display_name` lock from the player + trainer entity
 * statuses. Order is player first, then trainer (E26 — a name_change is filed
 * against `lockedBy[0]`).
 */
export function toDisplayNameLock(input: {
  readonly userId: string;
  readonly playerStatus: string | null | undefined;
  readonly trainerStatus: string | null | undefined;
}): DisplayNameLock {
  const lockedBy: NameLockHolder[] = [];
  if (input.playerStatus === 'verified')
    lockedBy.push({ entityType: 'player', entityId: input.userId });
  if (input.trainerStatus === 'verified')
    lockedBy.push({ entityType: 'trainer', entityId: input.userId });
  return { locked: lockedBy.length > 0, lockedBy };
}

/** The boolean render mark + display-only "since" date (E32). */
export type VerifiedMark = {
  readonly verified: boolean;
  /** Display only (hero "since" date). Null whenever `verified` is false. */
  readonly verifiedAt: string | null;
};

/**
 * E32 — converts an entity column (or owner state) into the public mark.
 * `verified` is true iff `status === 'verified'`; `verifiedAt` is normalised to
 * ISO and only returned when verified.
 */
export function toVerifiedMark(
  status: string | null | undefined,
  verifiedAt?: string | Date | null,
): VerifiedMark {
  const verified = status === 'verified';
  if (!verified || verifiedAt === null || verifiedAt === undefined) {
    return { verified, verifiedAt: null };
  }
  return {
    verified,
    verifiedAt: verifiedAt instanceof Date ? verifiedAt.toISOString() : verifiedAt,
  };
}

/** True when `flag` is one of the capping risk flags (E8). */
export function isCappingRiskFlag(flag: string): flag is VerificationCappingRiskFlag {
  return (VERIFICATION_CAPPING_RISK_FLAGS as readonly string[]).includes(flag);
}

/**
 * E8 — strength of an evaluator score (0–100). Any capping risk flag forces
 * `weak`; otherwise ≥75 strong, ≥50 moderate, else weak. A missing score is weak.
 */
export function strengthOf(
  score: number | null | undefined,
  riskFlags: ReadonlyArray<string> = [],
): VerificationStrength {
  if (riskFlags.some(isCappingRiskFlag)) return 'weak';
  if (score === null || score === undefined || Number.isNaN(score)) return 'weak';
  if (score >= STRENGTH_THRESHOLDS.strong) return 'strong';
  if (score >= STRENGTH_THRESHOLDS.moderate) return 'moderate';
  return 'weak';
}

/** Suffixes of the frozen native-mirror audit actions (E36). */
export const VERIFICATION_AUDIT_ACTION_SUFFIXES = [
  'approved',
  'rejected',
  'more_info_requested',
  'revoked',
  'direct_verified',
  'name_change_applied',
  'cooldown_lifted',
  'rerequest_unlocked',
  'name_lock_override',
  'candidate_invited',
  'candidate_dismissed',
  'watchlist_resolved',
] as const;
export type VerificationAuditActionSuffix = (typeof VERIFICATION_AUDIT_ACTION_SUFFIXES)[number];

/**
 * E36 — the native-mirror audit action for an entity type:
 *   player / trainer  → `user_admin_audit`   `user.verification_<suffix>`
 *   venue             → `venue_admin_audit`  `venue.verification_<suffix>`
 *   club / organizer  → `b2b_audit_log`      `verification.<suffix>` (target_kind `b2b_account`)
 */
export function verificationAuditAction(
  entityType: VerifiableEntityType,
  suffix: VerificationAuditActionSuffix,
): UserAdminAuditAction | VenueAdminAuditAction | B2bAuditAction {
  switch (entityType) {
    case 'player':
    case 'trainer':
      return `user.verification_${suffix}`;
    case 'venue':
      return `venue.verification_${suffix}`;
    case 'club':
    case 'organizer':
      return `verification.${suffix}`;
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// v2 — Zod inputs (all `.strict()`)
// ═════════════════════════════════════════════════════════════════════════════

const internalNoteField = z
  .string()
  .min(VERIFICATION_INTERNAL_NOTE_MIN)
  .max(VERIFICATION_INTERNAL_NOTE_MAX);
const reasonField = z.string().min(VERIFICATION_REASON_MIN).max(VERIFICATION_REASON_MAX);
const idDocumentNumberField = z.string().min(1).max(VERIFICATION_ID_NUMBER_MAX);
const issuingCountryField = z
  .string()
  .regex(/^[A-Z]{2}$/, 'Expected an uppercase ISO 3166-1 alpha-2 country code');

/**
 * `POST /me/verifications`. `entityId` is required for venue/club/organizer
 * (player/trainer default to the caller). `requestedName` is required for a
 * `name_change` and forbidden on an `initial` request; `requestedLegalName`
 * is only allowed for club/organizer name changes. Ownership, prerequisites
 * and "the name actually differs" (`NAME_UNCHANGED`) are service checks.
 */
export const CreateVerificationRequestInputSchema = z
  .object({
    entityType: VerifiableEntityTypeSchema,
    entityId: z.string().uuid().optional(),
    requestKind: VerificationRequestKindSchema,
    requestedName: z
      .string()
      .min(VERIFICATION_REQUESTED_NAME_MIN)
      .max(VERIFICATION_REQUESTED_NAME_MAX)
      .optional(),
    requestedLegalName: z
      .string()
      .min(VERIFICATION_REQUESTED_LEGAL_NAME_MIN)
      .max(VERIFICATION_REQUESTED_LEGAL_NAME_MAX)
      .optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const b2b: readonly VerifiableEntityType[] = VERIFICATION_B2B_ENTITY_TYPES;
    const legal: readonly VerifiableEntityType[] = VERIFICATION_LEGAL_NAME_ENTITY_TYPES;
    if (b2b.includes(v.entityType) && v.entityId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'entityId is required for venue, club and organizer requests',
      });
    }
    if (v.requestKind === 'name_change' && v.requestedName === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requestedName'],
        message: 'requestedName is required for a name_change request',
      });
    }
    if (v.requestKind === 'initial') {
      if (v.requestedName !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['requestedName'],
          message: 'requestedName is only allowed on a name_change request',
        });
      }
      if (v.requestedLegalName !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['requestedLegalName'],
          message: 'requestedLegalName is only allowed on a name_change request',
        });
      }
    }
    if (v.requestedLegalName !== undefined && !legal.includes(v.entityType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requestedLegalName'],
        message: 'requestedLegalName is only allowed for club and organizer requests',
      });
    }
  });
export type CreateVerificationRequestInput = z.infer<typeof CreateVerificationRequestInputSchema>;

/**
 * `POST /me/verifications/:id/documents/upload-url`. `mime` and `sizeBytes` are
 * deliberately NOT bounded here: the service rejects a non-allow-listed mime
 * with 422 `UNSUPPORTED_DOCUMENT_TYPE` and an oversized file with 422
 * `DOCUMENT_TOO_LARGE` (§8), which a Zod failure would mask as VALIDATION_ERROR.
 */
export const PresignVerificationDocumentInputSchema = z
  .object({
    kind: VerificationDocumentKindSchema,
    mime: z.string().min(1).max(127),
    sizeBytes: z.number().int().min(1),
    originalFilename: z.string().min(1).max(255),
  })
  .strict();
export type PresignVerificationDocumentInput = z.infer<
  typeof PresignVerificationDocumentInputSchema
>;

/** `POST /me/verifications/:id/documents` — finalize an uploaded object. */
export const AttachVerificationDocumentInputSchema = z
  .object({
    docId: z.string().uuid(),
    s3Key: z.string().min(1).max(512),
  })
  .strict();
export type AttachVerificationDocumentInput = z.infer<typeof AttachVerificationDocumentInputSchema>;

/**
 * The consent block echoed at submit (E21). `policyVersion` is a plain string —
 * a mismatch with the server-only policy version is 422 `CONSENT_VERSION_STALE`.
 */
export const VerificationConsentSchema = z
  .object({
    accepted: z.literal(true),
    policyVersion: z.string().min(1).max(100),
  })
  .strict();
export type VerificationConsent = z.infer<typeof VerificationConsentSchema>;

/** `POST /me/verifications/:id/submit` — submit, or resubmit after more-info. */
export const SubmitVerificationRequestInputSchema = z
  .object({
    requesterNote: z.string().max(VERIFICATION_NOTE_MAX).optional(),
    responseNote: z.string().max(VERIFICATION_NOTE_MAX).optional(),
    consent: VerificationConsentSchema,
  })
  .strict();
export type SubmitVerificationRequestInput = z.infer<typeof SubmitVerificationRequestInputSchema>;

/**
 * `POST /admin/verifications/:id/approve` (E23). The ID fields + `nameMatchConfirmed`
 * are REQUIRED for an `initial` request whose criteria include `government_id`,
 * but that is a service rule (422 `ID_NUMBER_REQUIRED`) — not enforced here.
 * The ID number is never persisted, logged or audited (B19); only its HMAC is.
 */
export const AdminApproveVerificationInputSchema = z
  .object({
    idDocumentNumber: idDocumentNumberField.optional(),
    idDocumentType: IdDocumentTypeSchema.optional(),
    idDocumentIssuingCountry: issuingCountryField.optional(),
    nameMatchConfirmed: z.literal(true).optional(),
    internalNote: internalNoteField.optional(),
    acknowledgeDuplicate: z.boolean().optional(),
  })
  .strict();
export type AdminApproveVerificationInput = z.infer<typeof AdminApproveVerificationInputSchema>;

/** `POST /admin/verifications/:id/reject` (E15). `internalNote` is never shown to the user. */
export const AdminRejectVerificationInputSchema = z
  .object({
    reasonCategory: VerificationReasonCategorySchema,
    note: z.string().max(VERIFICATION_NOTE_MAX).optional(),
    internalNote: internalNoteField.optional(),
  })
  .strict();
export type AdminRejectVerificationInput = z.infer<typeof AdminRejectVerificationInputSchema>;

/** `POST /admin/verifications/:id/request-more-info` (E42). */
export const AdminRequestMoreInfoInputSchema = z
  .object({
    prompt: z
      .string()
      .min(VERIFICATION_MORE_INFO_PROMPT_MIN)
      .max(VERIFICATION_MORE_INFO_PROMPT_MAX),
    requestedDocumentKinds: z
      .array(VerificationDocumentKindSchema)
      .max(VERIFICATION_DOCUMENT_KINDS.length)
      .optional(),
    dueInDays: z
      .number()
      .int()
      .min(VERIFICATION_MORE_INFO_DUE_DAYS_MIN)
      .max(VERIFICATION_MORE_INFO_DUE_DAYS_MAX)
      .optional(),
  })
  .strict();
export type AdminRequestMoreInfoInput = z.infer<typeof AdminRequestMoreInfoInputSchema>;

/** `POST /admin/verification-entities/:type/:id/revoke` (E16) — system categories rejected. */
export const AdminRevokeVerificationInputSchema = z
  .object({
    reasonCategory: AdminRevocationCategorySchema,
    reason: z.string().min(VERIFICATION_REASON_MIN).max(VERIFICATION_REVOCATION_REASON_MAX),
    internalNote: internalNoteField.optional(),
  })
  .strict();
export type AdminRevokeVerificationInput = z.infer<typeof AdminRevokeVerificationInputSchema>;

/**
 * `POST /admin/verification-entities/:type/:id/direct-verify` (super_admin). The
 * entity comes from the path. `acknowledgeDuplicate` mirrors approve so an ID
 * HMAC match on another human can be overridden (E9).
 */
export const AdminDirectVerifyInputSchema = z
  .object({
    internalNote: internalNoteField,
    idDocumentNumber: idDocumentNumberField.optional(),
    idDocumentType: IdDocumentTypeSchema.optional(),
    idDocumentIssuingCountry: issuingCountryField.optional(),
    acknowledgeDuplicate: z.boolean().optional(),
  })
  .strict();
export type AdminDirectVerifyInput = z.infer<typeof AdminDirectVerifyInputSchema>;

/** `POST /admin/verification-candidates/:id/invite`. */
export const AdminInviteCandidateInputSchema = z
  .object({ message: z.string().max(VERIFICATION_INVITE_MESSAGE_MAX).optional() })
  .strict();
export type AdminInviteCandidateInput = z.infer<typeof AdminInviteCandidateInputSchema>;

/** `POST /admin/verification-candidates/:id/dismiss`. */
export const AdminDismissCandidateInputSchema = z
  .object({
    reason: reasonField,
    resurfaceAfterDays: z
      .number()
      .int()
      .min(VERIFICATION_RESURFACE_AFTER_DAYS_MIN)
      .max(VERIFICATION_RESURFACE_AFTER_DAYS_MAX)
      .optional(),
  })
  .strict();
export type AdminDismissCandidateInput = z.infer<typeof AdminDismissCandidateInputSchema>;

/** `POST /admin/verification-watchlist/:id/resolve`. */
export const AdminResolveWatchlistInputSchema = z
  .object({
    resolution: WatchlistResolutionSchema,
    note: reasonField,
  })
  .strict();
export type AdminResolveWatchlistInput = z.infer<typeof AdminResolveWatchlistInputSchema>;

/** `POST /admin/verification-entities/:type/:id/lift-cooldown`. */
export const AdminLiftCooldownInputSchema = z.object({ reason: reasonField }).strict();
export type AdminLiftCooldownInput = z.infer<typeof AdminLiftCooldownInputSchema>;

/** `POST /admin/verification-entities/:type/:id/unlock-rerequest`. */
export const AdminUnlockRerequestInputSchema = z.object({ reason: reasonField }).strict();
export type AdminUnlockRerequestInput = z.infer<typeof AdminUnlockRerequestInputSchema>;

/**
 * Admin name-lock override payload (§11 — requires `verifications:review` plus the
 * host route's permission). Carried as `nameLockOverride` on the 4 admin edit routes.
 */
export const NameLockOverrideSchema = z.object({ reason: reasonField }).strict();
export type NameLockOverride = z.infer<typeof NameLockOverrideSchema>;

// ─── Query schemas ───────────────────────────────────────────────────────────

/** Accepts a repeated query param (`?status=a&status=b`) OR a CSV (`?status=a,b`). */
function csvOrRepeated<T extends z.ZodTypeAny>(item: T) {
  return z.preprocess(
    (v) =>
      typeof v === 'string'
        ? v
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        : v,
    z.array(item).min(1),
  );
}

const pageField = z.coerce.number().int().min(1);
const perPageField = z.coerce.number().int().min(1).max(100);

/** `GET /me/verifications?entityType&entityId`. */
export const OwnerVerificationListQuerySchema = z
  .object({
    entityType: VerifiableEntityTypeSchema.optional(),
    entityId: z.string().uuid().optional(),
  })
  .strict();
export type OwnerVerificationListQuery = z.infer<typeof OwnerVerificationListQuerySchema>;

/** `GET /admin/verifications` filters + sort (§6). Page-numbered (AdminPageMeta). */
export const AdminVerificationQueueQuerySchema = z
  .object({
    status: csvOrRepeated(VerificationRequestStatusSchema).optional(),
    entityType: VerifiableEntityTypeSchema.optional(),
    requestKind: VerificationRequestKindSchema.optional(),
    strength: VerificationStrengthSchema.optional(),
    hasDuplicateFlag: queryBoolean().optional(),
    countryCode: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .optional(),
    sort: z.enum(VERIFICATION_QUEUE_SORTS).optional(),
    page: pageField.optional(),
    perPage: perPageField.optional(),
  })
  .strict();
export type AdminVerificationQueueQuery = z.infer<typeof AdminVerificationQueueQuerySchema>;

/** `GET /admin/verifications/count?status`. */
export const AdminVerificationCountQuerySchema = z
  .object({ status: csvOrRepeated(VerificationRequestStatusSchema).optional() })
  .strict();
export type AdminVerificationCountQuery = z.infer<typeof AdminVerificationCountQuerySchema>;

/** `GET /admin/verification-entities?ownerUserId=` (E14). */
export const AdminVerificationEntitiesQuerySchema = z
  .object({ ownerUserId: z.string().uuid() })
  .strict();
export type AdminVerificationEntitiesQuery = z.infer<typeof AdminVerificationEntitiesQuerySchema>;

/** `GET /admin/verification-candidates`. */
export const AdminVerificationCandidatesQuerySchema = z
  .object({
    status: csvOrRepeated(CandidateStatusSchema).optional(),
    entityType: VerifiableEntityTypeSchema.optional(),
    page: pageField.optional(),
    perPage: perPageField.optional(),
  })
  .strict();
export type AdminVerificationCandidatesQuery = z.infer<
  typeof AdminVerificationCandidatesQuerySchema
>;

/** `GET /admin/verification-watchlist`. */
export const AdminVerificationWatchlistQuerySchema = z
  .object({
    status: csvOrRepeated(WatchlistStatusSchema).optional(),
    entityType: VerifiableEntityTypeSchema.optional(),
    reasonCode: WatchlistReasonCodeSchema.optional(),
    page: pageField.optional(),
    perPage: perPageField.optional(),
  })
  .strict();
export type AdminVerificationWatchlistQuery = z.infer<typeof AdminVerificationWatchlistQuerySchema>;

// ═════════════════════════════════════════════════════════════════════════════
// v2 — read shapes
// ═════════════════════════════════════════════════════════════════════════════

/**
 * E37 — owner-facing document receipt. Documents are NEVER re-served to the
 * requester (D5): no URL, no mime, no hash. `originalFilename` is returned only
 * while the document is unsubmitted (and only then may it be deleted).
 */
export type OwnerVerificationDocument = {
  readonly docId: string;
  readonly kind: VerificationDocumentKind;
  /** 1-based submission round (the current round while unsubmitted). */
  readonly round: number;
  readonly receivedAt: string;
  readonly received: true;
  readonly originalFilename: string | null;
};

/** One request as its owner sees it. Never carries `internalNote`, scores or risk flags. */
export type OwnerVerificationRequest = {
  readonly id: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly requestKind: VerificationRequestKind;
  readonly source: VerificationSource;
  readonly status: VerificationRequestStatus;
  readonly requestedName: string | null;
  readonly requestedLegalName: string | null;
  readonly requesterNote: string | null;
  readonly responseNote: string | null;
  readonly moreInfoPrompt: string | null;
  readonly moreInfoRequestedDocumentKinds: readonly VerificationDocumentKind[];
  readonly moreInfoRequestedAt: string | null;
  readonly moreInfoDueAt: string | null;
  readonly decisionReasonCategory: VerificationReasonCategory | null;
  /** User-visible decision note. */
  readonly decisionNote: string | null;
  readonly decidedAt: string | null;
  /** Set on rejected `initial` requests only (E26 — name changes have no cooldown). */
  readonly cooldownUntil: string | null;
  readonly revocationReasonCategory: VerificationRevocationCategory | null;
  readonly revocationReason: string | null;
  readonly revokedAt: string | null;
  readonly rerequestRequiresAdmin: boolean;
  readonly withdrawnReason: VerificationWithdrawnReason | null;
  readonly withdrawnAt: string | null;
  readonly submittedAt: string | null;
  readonly resubmittedAt: string | null;
  readonly resubmissionCount: number;
  readonly documents: readonly OwnerVerificationDocument[];
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Compact pointer to a request (admin venue rows, summaries). */
export type VerificationRequestRef = {
  readonly requestId: string;
  readonly requestKind: VerificationRequestKind;
  readonly status: VerificationRequestStatus;
  readonly submittedAt: string | null;
};

/** The `nameChange` sub-state of an owner state (E2 / E26). */
export type OwnerNameChangeSubState = {
  readonly state: OwnerNameChangeState;
  /** The open name_change request, if any. */
  readonly activeRequest: OwnerVerificationRequest | null;
  /** The latest terminal name_change request (e.g. "rejected — tick kept"). */
  readonly latestTerminal: OwnerVerificationRequest | null;
  /**
   * The entity the name_change is filed against. For the shared
   * `users.display_name` this is `lockedBy[0]` (player if held, else trainer)
   * and is shown on BOTH the player and the trainer owner states (E26).
   */
  readonly filedAgainst: NameLockHolder | null;
};

/**
 * E2 / E14 — the owner view of one entity (`GET /me/verification-targets`,
 * embedded in owner payloads as `verification`).
 */
export type OwnerEntityVerificationState = {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly state: OwnerVerificationState;
  readonly verifiedAt: string | null;
  /** The open `initial` request (draft / pending / more_info_needed). */
  readonly activeRequest: OwnerVerificationRequest | null;
  /** The latest terminal `initial` request (`legacy_reset` rows excluded — E2). */
  readonly latestTerminal: OwnerVerificationRequest | null;
  readonly cooldownUntil: string | null;
  readonly canRequest: boolean;
  readonly canRequestNameChange: boolean;
  /** Why `canRequest` is false; null when it is true. */
  readonly blockedReason: VerificationBlockedReason | null;
  readonly unmetPrerequisites: readonly VerificationPrerequisiteCode[];
  readonly nameChange: OwnerNameChangeSubState;
};

/** Compact cross-link summary (F43 user card, F42 venue card, QA samples). */
export type EntityVerificationSummary = {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly verificationStatus: EntityVerificationStatus;
  readonly verificationState: OwnerVerificationState;
  readonly verifiedAt: string | null;
  readonly openRequest: VerificationRequestRef | null;
  /** Admin SPA path: `/verifications/entities/:type/:id`. */
  readonly deepLink: string;
};

/** A duplicate match — by document SHA-256 or by ID-number HMAC (E9). Never carries the HMAC. */
export type VerificationDuplicateMatch = {
  readonly matchKind: 'document_sha' | 'identity_hmac';
  readonly requestId: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string | null;
  readonly requestStatus: VerificationRequestStatus;
  /** The matching document (document_sha only). */
  readonly docId: string | null;
  /** True when both sides are the same human (informational only — E9). */
  readonly sameSubject: boolean;
};

/**
 * E7 — admin document descriptor. There is NO URL: bytes are fetched through
 * the authenticated proxy at `contentPath` (every view writes an access-log row).
 */
export type AdminVerificationDocument = {
  readonly docId: string;
  readonly kind: VerificationDocumentKind;
  readonly mime: VerificationStoredDocMime;
  readonly sizeBytes: number;
  readonly sha256: string | null;
  /** Null for read-only viewers (no `verifications:review`). */
  readonly originalFilename: string | null;
  readonly round: number | null;
  readonly uploadedAt: string;
  readonly legacyV1: boolean;
  /** `/admin/verifications/:id/documents/:docId/content`. */
  readonly contentPath: string;
  /** False for read-only viewers ("Restricted"). */
  readonly canView: boolean;
  /** `download` for legacy HEIC (logged download only). */
  readonly renderMode: VerificationDocRenderMode;
  readonly duplicateMatches: readonly VerificationDuplicateMatch[];
};

/** One row of the admin request queue. */
export type AdminVerificationQueueItem = {
  readonly id: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly requestKind: VerificationRequestKind;
  readonly source: VerificationSource;
  readonly status: VerificationRequestStatus;
  readonly countryCode: string;
  readonly subjectUserId: string | null;
  readonly subjectDisplayName: string | null;
  readonly requesterUserId: string | null;
  readonly requesterDisplayName: string | null;
  readonly submittedAt: string | null;
  readonly queuedAt: string | null;
  readonly resubmissionCount: number;
  readonly moreInfoDueAt: string | null;
  readonly evaluatorScore: number | null;
  readonly strength: VerificationStrength | null;
  readonly riskFlags: readonly VerificationRiskFlag[];
  readonly hasDuplicateFlag: boolean;
  /** Short signal highlights for the "signals summary" column. */
  readonly signalHighlights: ReadonlyArray<{
    readonly key: string;
    readonly value: string | number | boolean | null;
  }>;
  readonly documentCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** One `verification_events` timeline row. Payloads never hold ID numbers. */
export type VerificationEventEntry = {
  readonly id: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly requestId: string | null;
  readonly candidateId: string | null;
  readonly eventKind: VerificationEventKind;
  readonly actorKind: VerificationEventActorKind;
  readonly actorUserId: string | null;
  readonly actorAdminId: string | null;
  readonly actorDisplayName: string | null;
  readonly reason: string | null;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly surface: string | null;
  readonly createdAt: string;
};

/** A prior request row in the admin "prior requests" panel (legacy_reset excluded). */
export type VerificationRequestHistoryItem = {
  readonly id: string;
  readonly requestKind: VerificationRequestKind;
  readonly source: VerificationSource;
  readonly status: VerificationRequestStatus;
  readonly submittedAt: string | null;
  readonly decidedAt: string | null;
  readonly decisionReasonCategory: VerificationReasonCategory | null;
  readonly revocationReasonCategory: VerificationRevocationCategory | null;
  readonly withdrawnReason: VerificationWithdrawnReason | null;
  readonly createdAt: string;
};

/** The ID-document metadata recorded at approve — never the number or the HMAC. */
export type VerificationIdDocumentRecord = {
  readonly docType: IdDocumentType;
  readonly issuingCountry: string;
  readonly recordedAt: string;
};

/**
 * Admin request detail (`GET /admin/verifications/:id`). No HMAC anywhere; has
 * `accessCount` + the events timeline.
 */
export type AdminVerificationRequestDetail = AdminVerificationQueueItem & {
  readonly requestedName: string | null;
  readonly requestedLegalName: string | null;
  /** E27 snapshots taken at submit. */
  readonly entityNameSnapshot: string | null;
  readonly entityLegalNameSnapshot: string | null;
  readonly identitySubjectNameSnapshot: string | null;
  /** Current names (a mismatch with the snapshot → 409 `NAME_CHANGED_DURING_REVIEW` at approve). */
  readonly currentEntityName: string;
  readonly currentEntityLegalName: string | null;
  readonly verifiedName: string | null;
  readonly verifiedLegalName: string | null;
  readonly requesterNote: string | null;
  readonly responseNote: string | null;
  readonly consentPolicyVersion: string | null;
  readonly consentAcceptedAt: string | null;
  readonly moreInfoPrompt: string | null;
  readonly moreInfoRequestedDocumentKinds: readonly VerificationDocumentKind[];
  readonly moreInfoRequestedAt: string | null;
  readonly moreInfoRequestedByAdminId: string | null;
  readonly decidedAt: string | null;
  readonly decidedByAdminId: string | null;
  readonly decisionReasonCategory: VerificationReasonCategory | null;
  readonly decisionNote: string | null;
  readonly adminInternalNote: string | null;
  readonly cooldownUntil: string | null;
  readonly cooldownLiftedAt: string | null;
  readonly revokedAt: string | null;
  readonly revokedByAdminId: string | null;
  readonly revocationReasonCategory: VerificationRevocationCategory | null;
  readonly revocationReason: string | null;
  readonly rerequestRequiresAdmin: boolean;
  readonly rerequestUnlockedAt: string | null;
  readonly withdrawnAt: string | null;
  readonly withdrawnReason: VerificationWithdrawnReason | null;
  readonly supersededByRequestId: string | null;
  readonly evaluatedAt: string | null;
  /** Live signals while open; the decision-time snapshot once decided (E10). */
  readonly evaluatorSignals: VerificationEvaluatorSignals | null;
  readonly unmetPrerequisites: readonly VerificationPrerequisiteCode[];
  /** Required groups not yet met (re-checked at approve — E23). */
  readonly missingDocumentGroups: readonly VerificationDocumentGroup[];
  readonly documents: readonly AdminVerificationDocument[];
  readonly idDocumentRecord: VerificationIdDocumentRecord | null;
  readonly identityDuplicates: readonly VerificationDuplicateMatch[];
  readonly priorRequests: readonly VerificationRequestHistoryItem[];
  /** F43 report history against the subject human (E40). */
  readonly reportHistory: {
    readonly openCount: number;
    readonly actionedCount365d: number;
  };
  /** Total document views on this request (access-log rows). */
  readonly accessCount: number;
  readonly events: readonly VerificationEventEntry[];
};

/** `GET /admin/verification-entities/:type/:id` — the entity-centric profile. */
export type AdminEntityVerificationProfile = {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly entityLegalName: string | null;
  /** The human whose ID is checked (owner for B2B entities). */
  readonly subjectUserId: string | null;
  readonly subjectDisplayName: string | null;
  readonly verificationStatus: EntityVerificationStatus;
  readonly verificationState: OwnerVerificationState;
  readonly verifiedAt: string | null;
  readonly verifiedByAdminId: string | null;
  readonly nameLocked: boolean;
  /** The live approved `initial` request backing the tick, if any. */
  readonly liveRequest: AdminVerificationQueueItem | null;
  readonly openRequests: readonly AdminVerificationQueueItem[];
  readonly requests: readonly VerificationRequestHistoryItem[];
  readonly cooldownUntil: string | null;
  readonly rerequestRequiresAdmin: boolean;
  readonly unmetPrerequisites: readonly VerificationPrerequisiteCode[];
  readonly candidate: VerificationCandidateView | null;
  readonly watchlist: VerificationCandidateView | null;
  readonly events: readonly VerificationEventEntry[];
};

/** One `verification_document_access_log` row (E7). */
export type VerificationAccessLogRow = {
  readonly id: string;
  readonly documentId: string;
  readonly requestId: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly accessedByAdminId: string | null;
  readonly accessedByDisplayName: string | null;
  readonly renderMode: VerificationDocRenderMode;
  readonly bytesServed: number;
  readonly context: string | null;
  readonly ip: string | null;
  readonly userAgent: string | null;
  readonly httpRequestId: string | null;
  readonly createdAt: string;
};

type VerificationCandidateViewBase = {
  readonly id: string;
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly score: number | null;
  readonly strength: VerificationStrength | null;
  readonly riskFlags: readonly VerificationRiskFlag[];
  readonly signals: VerificationEvaluatorSignals | null;
  readonly firstSeenAt: string;
  readonly computedAt: string;
  readonly signalsChangedAt: string | null;
  readonly invitedAt: string | null;
  readonly invitedByAdminId: string | null;
  readonly dismissedAt: string | null;
  readonly dismissedByAdminId: string | null;
  readonly dismissReason: string | null;
  readonly resolvedAt: string | null;
  readonly resolvedByAdminId: string | null;
  readonly resolutionNote: string | null;
  readonly resurfaceAfter: string | null;
  readonly watchedRequestId: string | null;
  readonly convertedRequestId: string | null;
};

/** A candidate or watchlist row (`verification_candidates`, discriminated by `kind`). */
export type VerificationCandidateView = VerificationCandidateViewBase &
  (
    | {
        readonly kind: 'candidate';
        readonly status: CandidateStatus;
        readonly reasonCodes: readonly CandidateReasonCode[];
      }
    | {
        readonly kind: 'watchlist';
        readonly status: WatchlistStatus;
        readonly reasonCodes: readonly WatchlistReasonCode[];
      }
  );

// ─── Evaluator signals (§9) ──────────────────────────────────────────────────

/** The shared risk block every signal branch carries. */
export type VerificationRiskSignals = {
  readonly openReports: number;
  readonly actionedReports365d: number;
  readonly priorRejections: number;
  readonly priorRevocations: number;
  readonly priorWithdrawals: number;
  readonly subjectSuspended: boolean;
  readonly duplicateIdentityHmac: boolean;
  readonly duplicateDocumentSha: boolean;
  readonly resubmittedIdenticalDocument: boolean;
  readonly nameChangedSinceSubmit: boolean;
  readonly flags: readonly VerificationRiskFlag[];
};

export type PlayerVerificationSignals = {
  readonly entityType: 'player';
  readonly accountAgeDays: number;
  readonly emailVerified: boolean;
  readonly phoneVerified: boolean;
  readonly maxEffectiveSkill: number | null;
  readonly totalMatchesPlayed: number;
  readonly hasAdminVerifiedSkill: boolean;
  readonly activeCaptaincies: number;
  readonly badgeCount: number;
  readonly profileCompletenessPercent: number;
  readonly risk: VerificationRiskSignals;
};

export type TrainerVerificationSignals = {
  readonly entityType: 'trainer';
  readonly verifiedCertCount: number;
  readonly pendingCertCount: number;
  readonly rejectedCertCount: number;
  readonly revokedCertCount: number;
  readonly completedSessions: number;
  readonly distinctClients: number;
  readonly bayesianRating: number | null;
  readonly reviewCount: number;
  readonly activeAffiliations: number;
  readonly verifiedAffiliatedVenues: number;
  readonly yearsExperience: number | null;
  readonly risk: VerificationRiskSignals;
};

export type VenueVerificationSignals = {
  readonly entityType: 'venue';
  readonly claimStatus: string;
  readonly claimApprovedAt: string | null;
  readonly subscriptionTier: string | null;
  readonly completedBookings90d: number;
  readonly profileCompletenessPercent: number;
  readonly approvedPhotos: number;
  readonly adminFlagged: boolean;
  readonly adminSuspended: boolean;
  readonly ownerPlayerVerified: boolean;
  readonly risk: VerificationRiskSignals;
};

export type ClubVerificationSignals = {
  readonly entityType: 'club';
  readonly managedTeams: number;
  readonly clubTrainers: number;
  readonly players: number;
  readonly completedTrainingSessions: number;
  readonly accountAgeDays: number;
  readonly legacyClubVerify: boolean;
  readonly risk: VerificationRiskSignals;
};

export type OrganizerVerificationSignals = {
  readonly entityType: 'organizer';
  readonly organizerProfileStatus: string | null;
  readonly organizerType: string | null;
  readonly tournamentsByStatus: Readonly<Record<string, number>>;
  readonly registrations: number;
  readonly approvedHosts: number;
  readonly risk: VerificationRiskSignals;
};

/** Evaluator signals — discriminated by `entityType`, plus the shared `risk` block. */
export type VerificationEvaluatorSignals =
  | PlayerVerificationSignals
  | TrainerVerificationSignals
  | VenueVerificationSignals
  | ClubVerificationSignals
  | OrganizerVerificationSignals;

/** One evaluator pass (`evaluate(ref)`). */
export type VerificationEvaluation = {
  readonly score: number;
  readonly riskFlags: readonly VerificationRiskFlag[];
  readonly strength: VerificationStrength;
  readonly signals: VerificationEvaluatorSignals;
  readonly evaluatedAt: string;
};

// ─── Explainer, upload, QA, list envelopes ───────────────────────────────────

export type VerificationDocumentLimits = {
  readonly maxBytes: number;
  readonly maxCount: number;
  readonly perKindMax: Readonly<Record<VerificationDocumentKind, number>>;
  readonly allowedMimes: readonly VerificationUploadMime[];
};

/** `GET /public/verification/explainer` (E21). Clients echo `policyVersion` in `consent`. */
export type VerificationExplainer = {
  readonly policyVersion: string;
  readonly rejectionCooldownDays: number;
  readonly criteria: readonly VerificationCriteria[];
  readonly documentLimits: VerificationDocumentLimits;
};

/** Presigned PUT returned by `POST /me/verifications/:id/documents/upload-url` (300 s). */
export type VerificationUploadUrlResponse = {
  readonly docId: string;
  readonly s3Key: string;
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly expiresAt: string;
};

/** `GET /admin/verification-qa` — the QA gallery + registry checklist payload. */
export type VerificationQaPayload = {
  readonly generatedAt: string;
  readonly surfaces: readonly VerificationRenderSurface[];
  readonly readShapeFields: readonly VerificationReadShapeField[];
  /** One live sample per entity type (null when none is verified yet). */
  readonly samples: Readonly<Record<VerifiableEntityType, EntityVerificationSummary | null>>;
  readonly counts: Readonly<
    Record<VerifiableEntityType, { readonly verified: number; readonly openRequests: number }>
  >;
};

export type AdminVerificationQueueResponse = {
  readonly data: readonly AdminVerificationQueueItem[];
  readonly meta: { readonly pagination: AdminPageMeta };
};

export type AdminVerificationCandidatesResponse = {
  readonly data: readonly VerificationCandidateView[];
  readonly meta: { readonly pagination: AdminPageMeta };
};

export type AdminVerificationCountResponse = {
  readonly data: { readonly count: number };
};

// ═════════════════════════════════════════════════════════════════════════════
// v2 — error codes (§5.4)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Every F09 v2 error code. `UNKNOWN_FIELD` (F42, reused unchanged for the
 * retired `verificationStatus` field edit) is intentionally not listed here.
 */
export const VERIFICATION_ERROR_CODES = [
  // 404
  'VERIFICATION_NOT_FOUND',
  'ENTITY_NOT_FOUND',
  'DOCUMENT_NOT_FOUND',
  'CANDIDATE_NOT_FOUND',
  'WATCHLIST_ENTRY_NOT_FOUND',
  // 403
  'NOT_ENTITY_OWNER',
  // 410
  'ENDPOINT_MOVED',
  // 422
  'VALIDATION_ERROR',
  'ENTITY_TYPE_MISMATCH',
  'PREREQUISITE_NOT_MET',
  'MISSING_REQUIRED_DOCUMENTS',
  'DOCUMENT_LIMIT_REACHED',
  'DOCUMENT_KIND_LIMIT_REACHED',
  'DOCUMENT_TOO_LARGE',
  'DOCUMENT_UPLOAD_MISSING',
  'UNSUPPORTED_DOCUMENT_TYPE',
  'S3_KEY_MISMATCH',
  'CONSENT_REQUIRED',
  'CONSENT_VERSION_STALE',
  'NAME_UNCHANGED',
  'NAME_LOCKED_VERIFIED',
  'ID_NUMBER_REQUIRED',
  // 409
  'ALREADY_VERIFIED',
  'NOT_VERIFIED',
  'REQUEST_ALREADY_OPEN',
  'COOLDOWN_ACTIVE',
  'REREQUEST_REQUIRES_SUPPORT',
  'NO_ACTIVE_COOLDOWN',
  'REREQUEST_NOT_GATED',
  'INVALID_STATUS_TRANSITION',
  'DOCUMENTS_LOCKED',
  'VERIFICATION_DOC_CHANGED',
  'NAME_CHANGED_DURING_REVIEW',
  'DUPLICATE_IDENTITY',
  'CANDIDATE_INVITE_RATE_LIMITED',
  'CANDIDATES_DISABLED',
  // 429
  'RATE_LIMITED',
] as const;
export type VerificationErrorCode = (typeof VERIFICATION_ERROR_CODES)[number];

export type VerificationErrorHttpStatus = 403 | 404 | 409 | 410 | 422 | 429;

/**
 * Default HTTP status per code. `PREREQUISITE_NOT_MET` defaults to 422 (create /
 * submit); the API OVERRIDES it to 409 at approve and direct-verify (E12) — see
 * `PREREQUISITE_NOT_MET_HTTP_STATUS_BY_PHASE`.
 */
export const VERIFICATION_ERROR_HTTP_STATUS: Readonly<
  Record<VerificationErrorCode, VerificationErrorHttpStatus>
> = {
  VERIFICATION_NOT_FOUND: 404,
  ENTITY_NOT_FOUND: 404,
  DOCUMENT_NOT_FOUND: 404,
  CANDIDATE_NOT_FOUND: 404,
  WATCHLIST_ENTRY_NOT_FOUND: 404,
  NOT_ENTITY_OWNER: 403,
  ENDPOINT_MOVED: 410,
  VALIDATION_ERROR: 422,
  ENTITY_TYPE_MISMATCH: 422,
  PREREQUISITE_NOT_MET: 422,
  MISSING_REQUIRED_DOCUMENTS: 422,
  DOCUMENT_LIMIT_REACHED: 422,
  DOCUMENT_KIND_LIMIT_REACHED: 422,
  DOCUMENT_TOO_LARGE: 422,
  DOCUMENT_UPLOAD_MISSING: 422,
  UNSUPPORTED_DOCUMENT_TYPE: 422,
  S3_KEY_MISMATCH: 422,
  CONSENT_REQUIRED: 422,
  CONSENT_VERSION_STALE: 422,
  NAME_UNCHANGED: 422,
  NAME_LOCKED_VERIFIED: 422,
  ID_NUMBER_REQUIRED: 422,
  ALREADY_VERIFIED: 409,
  NOT_VERIFIED: 409,
  REQUEST_ALREADY_OPEN: 409,
  COOLDOWN_ACTIVE: 409,
  REREQUEST_REQUIRES_SUPPORT: 409,
  NO_ACTIVE_COOLDOWN: 409,
  REREQUEST_NOT_GATED: 409,
  INVALID_STATUS_TRANSITION: 409,
  DOCUMENTS_LOCKED: 409,
  VERIFICATION_DOC_CHANGED: 409,
  NAME_CHANGED_DURING_REVIEW: 409,
  DUPLICATE_IDENTITY: 409,
  CANDIDATE_INVITE_RATE_LIMITED: 409,
  CANDIDATES_DISABLED: 409,
  RATE_LIMITED: 429,
};

/** E12 — `PREREQUISITE_NOT_MET` is 422 at create/submit and 409 at approve/direct-verify. */
export const PREREQUISITE_NOT_MET_HTTP_STATUS_BY_PHASE: Readonly<
  Record<VerificationPrerequisitePhase, 409 | 422>
> = {
  create: 422,
  submit: 422,
  approve: 409,
  direct_verify: 409,
};

/** `PREREQUISITE_NOT_MET` details (E12 — prerequisite failures appear ONLY here). */
export type PrerequisiteNotMetDetails = {
  readonly failures: readonly VerificationPrerequisiteCode[];
};

/** `NAME_LOCKED_VERIFIED` details (E13). */
export type NameLockedVerifiedDetails = {
  readonly field: VerificationNameField;
  readonly lockedBy: readonly NameLockHolder[];
  readonly nameChangeRequest: {
    readonly method: 'POST';
    readonly path: '/me/verifications';
    readonly requestKind: 'name_change';
  };
};

/** `MISSING_REQUIRED_DOCUMENTS` details. */
export type MissingRequiredDocumentsDetails = {
  readonly missing: readonly VerificationDocumentGroup[];
};

/** `DUPLICATE_IDENTITY` details (E9 — never the HMAC or the number). */
export type DuplicateIdentityDetails = {
  readonly matches: readonly VerificationDuplicateMatch[];
};

/** `COOLDOWN_ACTIVE` details. */
export type CooldownActiveDetails = { readonly cooldownUntil: string };

/** `REQUEST_ALREADY_OPEN` details. */
export type RequestAlreadyOpenDetails = { readonly requestId: string };

/** `NAME_CHANGED_DURING_REVIEW` details (E27). */
export type NameChangedDuringReviewDetails = {
  readonly snapshotName: string | null;
  readonly currentName: string;
};

/** Error-details payload per code (codes not listed carry no details). */
export type VerificationErrorDetailsMap = {
  readonly PREREQUISITE_NOT_MET: PrerequisiteNotMetDetails;
  readonly NAME_LOCKED_VERIFIED: NameLockedVerifiedDetails;
  readonly MISSING_REQUIRED_DOCUMENTS: MissingRequiredDocumentsDetails;
  readonly DUPLICATE_IDENTITY: DuplicateIdentityDetails;
  readonly COOLDOWN_ACTIVE: CooldownActiveDetails;
  readonly REQUEST_ALREADY_OPEN: RequestAlreadyOpenDetails;
  readonly NAME_CHANGED_DURING_REVIEW: NameChangedDuringReviewDetails;
};

// ═════════════════════════════════════════════════════════════════════════════
// v2 — registries (§13 render surfaces, §5.3 read-shape fields)
// ═════════════════════════════════════════════════════════════════════════════

export type VerificationSurfacePlatform = 'portal' | 'mobile' | 'marketing' | 'admin';
export type VerificationFrontendTrack = '2D' | '2E' | '2F';
export type VerificationBackendTrack = '2B' | '2C';

/** One §13 render surface on one platform (rows spanning platforms become several entries). */
export type VerificationRenderSurface = {
  /** The §13 table row number (shared by the per-platform entries of one row). */
  readonly id: number;
  readonly surface: string;
  readonly platform: VerificationSurfacePlatform;
  readonly fileHint: string;
  readonly entityType: VerifiableEntityType | 'all';
  readonly payloadField: string;
  readonly track: VerificationFrontendTrack;
};

/** Every P0 tick surface (§13). The admin QA page renders this as its checklist. */
export const VERIFICATION_RENDER_SURFACES: readonly VerificationRenderSurface[] = [
  {
    id: 1,
    surface: 'Portal player profile /u/:id',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/ProfilePage.tsx:163',
    entityType: 'player',
    payloadField: 'profile.verified',
    track: '2E',
  },
  {
    id: 2,
    surface: 'Portal own profile (hand-drawn pill removed)',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/MyProfilePage.tsx:137-144',
    entityType: 'player',
    payloadField: 'profile.verified',
    track: '2E',
  },
  {
    id: 3,
    surface: 'Mobile public profile (pill removed)',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/Users/PublicProfileScreen.tsx:204-223',
    entityType: 'player',
    payloadField: 'profile.verified',
    track: '2D',
  },
  {
    id: 4,
    surface: 'Mobile own profile + settings row',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/ProfileTab/MyProfileScreen.tsx:248, :284-288',
    entityType: 'player',
    payloadField: 'profile.verified / verification-targets',
    track: '2D',
  },
  {
    id: 5,
    surface: 'Open-game participants + "Hosted by"',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/OpenGames/OpenGameDetailScreen.tsx:66-98, :444-451',
    entityType: 'player',
    payloadField: 'verified / postedByVerified',
    track: '2D',
  },
  {
    id: 6,
    surface: 'Captain inbox',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/OpenGames/CaptainInboxScreen.tsx:232',
    entityType: 'player',
    payloadField: 'requesterVerified',
    track: '2D',
  },
  {
    id: 7,
    surface: 'Team members',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/me/teams/[teamId].tsx:332',
    entityType: 'player',
    payloadField: 'verified',
    track: '2E',
  },
  {
    id: 7,
    surface: 'Team members',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/Teams/TeamProfileScreen.tsx:300',
    entityType: 'player',
    payloadField: 'verified',
    track: '2D',
  },
  {
    id: 7,
    surface: 'Team members (admin pill removed)',
    platform: 'admin',
    fileHint: 'apps/admin/src/pages/teams/[teamId].tsx:141-148',
    entityType: 'player',
    payloadField: 'verified',
    track: '2F',
  },
  {
    id: 8,
    surface: 'Club-trainer roster',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/business/club-trainer/RosterPage.tsx:172-181',
    entityType: 'player',
    payloadField: 'verified',
    track: '2E',
  },
  {
    id: 8,
    surface: 'Club-trainer roster',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/ClubTrainer/ClubTrainerRosterScreen.tsx:91-97',
    entityType: 'player',
    payloadField: 'verified',
    track: '2D',
  },
  {
    id: 9,
    surface: 'Review authors',
    platform: 'portal',
    fileHint:
      'apps/portal/src/pages/business/trainer-dashboard/reviews/ReviewList.tsx:53-66 (+ apps/portal/src/components/recommendation/ProfileReviewsSection.tsx)',
    entityType: 'player',
    payloadField: 'authorVerified',
    track: '2E',
  },
  {
    id: 9,
    surface: 'Review authors',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/TrainerDashboard/components/EndorseClientSection.tsx:293',
    entityType: 'player',
    payloadField: 'authorVerified',
    track: '2D',
  },
  {
    id: 10,
    surface: 'Review subject (trainer)',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/Reviews/SubmitReviewScreen.tsx:137, apps/mobile/src/screens/MainStack/Reviews/components/ReviewPromptBottomSheet.tsx:26,113, mobile trainer profile header',
    entityType: 'trainer',
    payloadField: 'trainerVerified / verified',
    track: '2D',
  },
  {
    id: 11,
    surface: 'Chat sender (identity map from the sidecars)',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/Chat/ChatRoomScreen.tsx:725',
    entityType: 'player',
    payloadField: 'authors[id].verified',
    track: '2D',
  },
  {
    id: 12,
    surface: 'Trainer profile + marketplace card',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/Trainers/TrainerProfileScreen.tsx:186-190, apps/mobile/src/screens/MainStack/Trainers/TrainerMarketplaceScreen.tsx:182-186',
    entityType: 'trainer',
    payloadField: 'verified',
    track: '2D',
  },
  {
    id: 13,
    surface: 'Trainer own edit header',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/business/trainer-profile/TrainerProfileEditPage.tsx:263',
    entityType: 'trainer',
    payloadField: 'verification (owner state)',
    track: '2E',
  },
  {
    id: 14,
    surface: 'Venue cards — portal discovery',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/VenuesDiscoveryPage.tsx:401',
    entityType: 'venue',
    payloadField: 'verified',
    track: '2E',
  },
  {
    id: 14,
    surface: 'Venue cards — mobile discovery + 3 placement slots',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/Discovery/DiscoveryScreen.tsx:345, apps/mobile/src/api/placements.ts:126-129',
    entityType: 'venue',
    payloadField: 'verified',
    track: '2D',
  },
  {
    id: 14,
    surface: 'Venue cards — marketing discover',
    platform: 'marketing',
    fileHint: 'apps/marketing/src/app/venues/discover/DiscoverClient.tsx:519',
    entityType: 'venue',
    payloadField: 'verified',
    track: '2E',
  },
  {
    id: 15,
    surface: 'Venue profile (re-gated)',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/PortalVenueDetailPage.tsx:340',
    entityType: 'venue',
    payloadField: 'venue.verified',
    track: '2E',
  },
  {
    id: 15,
    surface: 'Venue profile',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/Venues/VenueDetailScreen.tsx:351',
    entityType: 'venue',
    payloadField: 'venue.verified',
    track: '2D',
  },
  {
    id: 16,
    surface: 'Venue profile, marketing (live-checked island)',
    platform: 'marketing',
    fileHint: 'apps/marketing/src/app/v/[slug]/page.tsx:298 + VerifiedVenueTickLive.tsx',
    entityType: 'venue',
    payloadField: 'public detail fetched at load',
    track: '2E',
  },
  {
    id: 17,
    surface: 'F12 editor header + name-lock banner',
    platform: 'portal',
    fileHint:
      'apps/portal/src/pages/business/VenueEditorPage.tsx:157-158, apps/portal/src/pages/business/venue-editor/BasicsTab.tsx:357-359',
    entityType: 'venue',
    payloadField: 'verified, nameLocked',
    track: '2E',
  },
  {
    id: 18,
    surface: 'Club detail (B2B)',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/business/club/ClubDetailPage.tsx:291-293',
    entityType: 'club',
    payloadField: 'verified',
    track: '2E',
  },
  {
    id: 18,
    surface: 'Player club list',
    platform: 'mobile',
    fileHint: 'apps/mobile/src/screens/MainStack/Club/ClubAffiliationsScreen.tsx:131',
    entityType: 'club',
    payloadField: 'clubVerified',
    track: '2D',
  },
  {
    id: 19,
    surface: 'Tournament card + "Organized by ✓"',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/Tournaments/TournamentMarketplaceScreen.tsx:204-218, apps/mobile/src/screens/MainStack/Tournaments/TournamentDetailScreen.tsx:203',
    entityType: 'club',
    payloadField: 'organizerVerified',
    track: '2D',
  },
  {
    id: 19,
    surface: 'Tournament card + "Organized by ✓"',
    platform: 'mobile',
    fileHint:
      'apps/mobile/src/screens/MainStack/Tournaments/TournamentMarketplaceScreen.tsx:204-218, apps/mobile/src/screens/MainStack/Tournaments/TournamentDetailScreen.tsx:203',
    entityType: 'organizer',
    payloadField: 'organizerVerified',
    track: '2D',
  },
  {
    id: 20,
    surface: 'Portal organizer dashboard header',
    platform: 'portal',
    fileHint: 'apps/portal/src/pages/business/tournaments/*',
    entityType: 'organizer',
    payloadField: 'own state',
    track: '2E',
  },
  {
    id: 21,
    surface: 'Admin tables (venues, users, clubs, trainer pages, review moderation)',
    platform: 'admin',
    fileHint:
      'apps/admin/src/pages/AdminVenuesPage.tsx:535-544, apps/admin/src/pages/AdminVenueDetailPage.tsx:1577-1584, apps/admin/src/pages/platform-users/index.tsx:369-377, apps/admin/src/pages/platform-users/[userId].tsx:524-528, apps/admin/src/pages/clubs/[clubId].tsx:399-420, apps/admin/src/pages/trainer-verifications/[userId].tsx:266, review moderation table',
    entityType: 'all',
    payloadField: 'verificationState / booleans',
    track: '2F',
  },
];

/** One §5.3 read-shape row (rows with two producers/tracks are split per producer). */
export type VerificationReadShapeField = {
  readonly shape: string;
  readonly fields: readonly string[];
  readonly source: string;
  readonly producer: string;
  readonly track: VerificationBackendTrack;
};

/** The §5.3 producer contract: which payload gains which tick field, produced by which track. */
export const VERIFICATION_READ_SHAPE_FIELDS: readonly VerificationReadShapeField[] = [
  {
    shape: 'SportsProfile* / UserSearchResult',
    fields: ['verified'],
    source: 'users',
    producer: 'apps/api/src/services/sports-profile.ts',
    track: '2C',
  },
  {
    shape: 'MeResponse',
    fields: ['verified', 'displayNameLock'],
    source: 'users + trainer_profiles',
    producer: 'apps/api/src/services/user.ts',
    track: '2B',
  },
  {
    shape: 'TeamMemberWithUser',
    fields: ['verified'],
    source: 'users',
    producer: 'apps/api/src/services/team.ts, apps/api/src/services/club-team.ts',
    track: '2C',
  },
  {
    shape: 'OpenGameDetail / OpenGameParticipantView / CaptainInboxItem',
    fields: ['postedByVerified', 'verified', 'requesterVerified'],
    source: 'users',
    producer: 'apps/api/src/services/open-game*.ts',
    track: '2C',
  },
  {
    shape: 'TrainerReviewPublic',
    fields: ['authorVerified'],
    source: 'users',
    producer: 'apps/api/src/services/review.ts',
    track: '2C',
  },
  {
    shape: 'TrainerReviewAdmin + ReviewPrompt / review composer params',
    fields: ['trainerVerified'],
    source: 'trainer_profiles',
    producer: 'apps/api/src/services/review.ts',
    track: '2C',
  },
  {
    shape: 'ChatMessagePage / room:join ack / message:created',
    fields: ['authors', 'author'],
    source: 'users',
    producer:
      'apps/api/src/services/chat-message.ts, apps/api/src/services/chat-poll.ts, apps/api/src/services/chat-socket-server.ts',
    track: '2C',
  },
  {
    shape: 'PublicTrainerProfile',
    fields: ['verified', 'verifiedAt'],
    source: 'trainer_profiles',
    producer: 'apps/api/src/services/trainer-profile.ts',
    track: '2B',
  },
  {
    shape: 'TrainerSearchResultItem',
    fields: ['verified', 'verifiedAt'],
    source: 'trainer_profiles',
    producer: 'apps/api/src/services/trainer-marketplace.ts',
    track: '2C',
  },
  {
    shape: 'TrainerProfile (owner)',
    fields: ['verification'],
    source: 'requests',
    producer: 'apps/api/src/services/trainer-profile.ts (via W1 owner-state.ts)',
    track: '2B',
  },
  {
    shape: 'PublicAffiliatedTrainer',
    fields: ['verified'],
    source: 'trainer_profiles',
    producer: 'apps/api/src/services/venue.ts',
    track: '2C',
  },
  {
    shape: 'VenueListItem / Venue public detail',
    fields: ['verified', 'verifiedAt'],
    source: 'venues',
    producer: 'apps/api/src/services/venue.ts',
    track: '2C',
  },
  {
    shape: 'VenueProfileEditorPayload (F12 editor)',
    fields: ['verified', 'nameLocked'],
    source: 'venues',
    producer: 'apps/api/src/services/venue-profile.ts',
    track: '2C',
  },
  {
    shape: 'AdminVenueListRow / AdminVenueDetail',
    fields: ['verificationState', 'openVerificationRequest'],
    source: 'venues + requests',
    producer: 'apps/api/src/services/venue-admin.ts',
    track: '2B',
  },
  {
    shape: 'ClubDetail / ClubSummary / PlayerClubAffiliationRow (/me/club-affiliations)',
    fields: ['verified', 'clubVerified'],
    source: 'b2b_accounts',
    producer: 'apps/api/src/services/club.ts, apps/api/src/routes/me-club.ts',
    track: '2B',
  },
  {
    shape: 'B2bAccount',
    fields: ['verificationStatus', 'verifiedAt', 'nameLocked'],
    source: 'b2b_accounts',
    producer: 'apps/api/src/services/b2b-account.ts',
    track: '2B',
  },
  {
    shape: 'TournamentSummary / TournamentDetail / SponsoredTournamentRef',
    fields: ['organizerDisplayName', 'organizerVerified'],
    source: 'b2b_accounts',
    producer: 'apps/api/src/services/tournament*.ts',
    track: '2C',
  },
  {
    shape: 'AdminUserListItem / AdminUserDetail',
    fields: ['playerVerified', 'trainerVerified'],
    source: 'users + trainer_profiles',
    producer: 'apps/api/src/services/admin-user.service.ts',
    track: '2B',
  },
  {
    shape: 'TrainerDashboardHome',
    fields: ['verification'],
    source: 'requests',
    producer: 'apps/api/src/services/trainer-dashboard.ts',
    track: '2B',
  },
];
